import {
  PaymentIntegration,
  CreatePaymentIntegrationData,
  UpdatePaymentIntegrationData,
  PaymentIntegrationFilters,
} from "@lemmonhq/types";
import { getAPIClient } from "../helpers";

export type PaymentIntegrationsListResponse = {
  data: PaymentIntegration[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    total_pages?: number;
  };
};

export const paymentIntegrationsAPI = {
  getPaymentIntegrations: async (
    filters?: PaymentIntegrationFilters,
  ): Promise<PaymentIntegrationsListResponse> => {
    const hc = getAPIClient();

    const query: Record<string, unknown> = {};

    if (filters?.type) {
      query.type = filters.type;
    }

    const resp = await hc.api.v2.payment_integrations.$get({
      query: query as Record<string, string | string[]>,
    });

    if (resp.ok) {
      const json: any = await resp.json();
      const rawData = json?.data;

      const data: PaymentIntegration[] = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.data)
          ? rawData.data
          : [];

      const paginationSource =
        (rawData && typeof rawData === "object" && rawData.pagination) ||
        json?.pagination ||
        {};

      return {
        data,
        pagination: {
          total:
            typeof paginationSource.total === "number"
              ? paginationSource.total
              : data.length,
          limit:
            typeof paginationSource.limit === "number"
              ? paginationSource.limit
              : 20,
          offset:
            typeof paginationSource.offset === "number"
              ? paginationSource.offset
              : 0,
          ...(paginationSource.total_pages !== undefined
            ? { total_pages: Number(paginationSource.total_pages) }
            : {}),
        },
      };
    }
    throw new Error("Failed to fetch payment integrations");
  },

  getPaymentIntegration: async (
    integrationId: string,
  ): Promise<PaymentIntegration> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.payment_integrations[":id"].$get({
      param: { id: integrationId },
    });

    if (resp.ok) {
      return (await resp.json()) as PaymentIntegration;
    }
    throw new Error("Failed to fetch payment integration");
  },

  createPaymentIntegration: async (
    data: CreatePaymentIntegrationData,
  ): Promise<PaymentIntegration> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.payment_integrations.$post({
      json: data,
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json?.data)) {
        return json.data[0] as PaymentIntegration;
      }
      if (json?.data) {
        return json.data as PaymentIntegration;
      }
      return json as PaymentIntegration;
    }
    throw new Error("Failed to create payment integration");
  },

  updatePaymentIntegration: async (
    integrationId: string,
    data: UpdatePaymentIntegrationData,
  ): Promise<PaymentIntegration> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.payment_integrations[":id"].$patch({
      param: { id: integrationId },
      json: data,
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json?.data)) {
        return json.data[0] as PaymentIntegration;
      }
      if (json?.data) {
        return json.data as PaymentIntegration;
      }
      return json as PaymentIntegration;
    }
    throw new Error("Failed to update payment integration");
  },

  deletePaymentIntegration: async (
    integrationId: string,
  ): Promise<{ success: boolean }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.payment_integrations[":id"].$delete({
      param: { id: integrationId },
    });

    if (resp.ok) {
      return (await resp.json()) as { success: boolean };
    }
    throw new Error("Failed to delete payment integration");
  },
};
